import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { getReviewPhotoUrl } from "@/lib/review-photos.functions";

export type ProductReview = {
  id: string;
  product_slug: string;
  author_first_name: string;
  rating: number;
  comment: string | null;
  photo_url: string | null;
  verified_purchase: boolean;
  created_at: string;
};

export type ReviewStats = {
  count: number;
  average: number;
  /** Répartition 1★ → 5★ (index 0 = 1★). */
  distribution: number[];
};

export const REVIEW_BUCKET = "review-photos";

export function statsFromRatings(ratings: number[]): ReviewStats {
  const distribution = [0, 0, 0, 0, 0];
  ratings.forEach((r) => {
    const i = Math.min(5, Math.max(1, Math.round(r))) - 1;
    distribution[i] = (distribution[i] ?? 0) + 1;
  });
  const count = ratings.length;
  const average = count ? ratings.reduce((s, r) => s + r, 0) / count : 0;
  return { count, average, distribution };
}

/** Formate une date ISO en français (ex. « 10 août 2026 »). */
export function formatReviewDate(iso: string): string {
  return new Date(iso).toLocaleDateString("fr-FR", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

/** Avis publiés d'un produit, du plus récent au plus ancien. */
export function useProductReviews(productSlug: string) {
  return useQuery({
    queryKey: ["product-reviews", productSlug],
    queryFn: async (): Promise<ProductReview[]> => {
      const { data, error } = await supabase
        .from("product_reviews")
        .select("id, product_slug, author_first_name, rating, comment, photo_url, verified_purchase, created_at")
        .eq("product_slug", productSlug)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as ProductReview[];
    },
  });
}

/** Notes moyennes de tous les produits, pour les cartes des collections. */
export function useAllReviewStats() {
  return useQuery({
    queryKey: ["product-review-stats"],
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<Record<string, ReviewStats>> => {
      const { data, error } = await supabase
        .from("product_reviews")
        .select("product_slug, rating");
      if (error) throw error;
      const grouped: Record<string, number[]> = {};
      (data ?? []).forEach((row) => {
        const slug = row.product_slug as string;
        (grouped[slug] ??= []).push(row.rating as number);
      });
      return Object.fromEntries(
        Object.entries(grouped).map(([slug, ratings]) => [slug, statsFromRatings(ratings)]),
      );
    },
  });
}

export type NewReview = {
  productSlug: string;
  firstName: string;
  rating: number;
  comment: string;
  photo?: File | null;
};

/** Envoi d'un avis client (photo optionnelle stockée dans le bucket dédié). */
export function useSubmitReview(productSlug: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (review: NewReview) => {
      let photoPath: string | null = null;

      if (review.photo) {
        const allowed = ["image/jpeg", "image/png", "image/webp", "image/avif"];
        if (!allowed.includes(review.photo.type)) {
          throw new Error("Format d'image non pris en charge (JPG, PNG, WEBP ou AVIF).");
        }
        if (review.photo.size > 5 * 1024 * 1024) {
          throw new Error("La photo ne doit pas dépasser 5 Mo.");
        }
        const extByType: Record<string, string> = {
          "image/jpeg": "jpg",
          "image/png": "png",
          "image/webp": "webp",
          "image/avif": "avif",
        };
        const ext = extByType[review.photo.type]!;
        const path = `${review.productSlug}/${crypto.randomUUID()}.${ext}`;
        const { error: uploadError } = await supabase.storage
          .from(REVIEW_BUCKET)
          .upload(path, review.photo, { contentType: review.photo.type, upsert: false });
        if (uploadError) throw uploadError;
        photoPath = path;
      }

      const { error } = await supabase.from("product_reviews").insert({
        product_slug: review.productSlug,
        author_first_name: review.firstName.trim(),
        rating: review.rating,
        comment: review.comment.trim() ? review.comment.trim() : null,
        photo_url: photoPath,
      });
      if (error) throw error;
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["product-reviews", productSlug] }),
        queryClient.invalidateQueries({ queryKey: ["product-review-stats"] }),
      ]);
    },
  });
}

/** Lien temporaire vers une photo d'avis (bucket privé). */
export function useReviewPhotoUrl(path: string | null) {
  return useQuery({
    queryKey: ["review-photo", path],
    enabled: Boolean(path),
    staleTime: 30 * 60_000,
    queryFn: async () => {
      const { url } = await getReviewPhotoUrl({ data: { path: path! } });
      return url;
    },
  });
}
